import React from "react";

/**
 * Live Routing Topology — animated lead-routing infrastructure diagram.
 *
 * Visual story we want to tell:
 *   Sources (4) → CONNEXIS Ping Tree → Buyer panel (4 nodes)
 *
 * Animation choreography (single 4.4s loop, perfectly staggered):
 *   1. Source pulses, packet emits and travels to the ping tree (4 packets in parallel,
 *      one per source line, staggered 0.4s).
 *   2. Ping tree absorbs packet → gentle blue ring expands from centre.
 *   3. Ping tree emits 4 outbound packets to buyers (lit ~1.2s after source emit so
 *      sources and buyers never feel disjointed).
 *   4. Each buyer rectangle gets a quick "received" pulse on its left edge.
 *   5. ONE buyer at a time holds a soft emerald ring to indicate the lead was
 *      routed to that buyer at the FIXED sector CPL — rotates among the 4 over
 *      the loop so all feel live. Strictly no bidding language.
 *
 * Layout fixes vs. previous version:
 *   • Each buyer card is now TWO LINES — label on top, price below — so long names
 *     like "Beacon Finance" can never collide with the price.
 *   • Source + buyer cards align on a common 90px vertical pitch (was 70) — gives
 *     the diagram room to breathe at 1920px and at every responsive break.
 *   • SVG viewBox shaped to actual aspect (640 × 380) so cards don't pile up.
 */
export default function DataFlowDiagram() {
  const SOURCES = [
    { y: 60, label: "Paid Search" },
    { y: 150, label: "Affiliate" },
    { y: 240, label: "SEO Funnel" },
    { y: 330, label: "WhatsApp AI" },
  ];
  const BUYERS = [
    { y: 60,  label: "Aspect Capital", sector: "Mortgages",        price: "£38" },
    { y: 150, label: "Beacon Finance", sector: "Business Finance", price: "£52" },
    { y: 240, label: "Pillar Life",    sector: "Life Insurance",   price: "£34" },
    { y: 330, label: "Resolve Debt",   sector: "Debt Help",        price: "£28" },
  ];

  // Geometry constants
  const SRC_X = 20;            // source box x
  const SRC_W = 130;           // source box width
  const SRC_RIGHT = SRC_X + SRC_W; // 150
  const CORE_X = 270;          // ping tree x
  const CORE_W = 100;          // ping tree width
  const CORE_RIGHT = CORE_X + CORE_W; // 370
  const CORE_Y_MID = 195;      // ping tree vertical centre
  const BUYER_X = 470;         // buyer box x
  const BUYER_W = 150;         // buyer box width
  const BUYER_H = 48;          // taller two-line card
  const BUYER_RIGHT = BUYER_X + BUYER_W; // 620

  // Cycle timing — single source of truth
  const CYCLE = 4.4;           // total loop, seconds
  const SRC_DUR = 1.8;         // travel time source → core
  const BUYER_DUR = 1.6;       // travel time core → buyer
  const HANDOFF_GAP = 0.6;     // overlap so the relay feels continuous

  // Helper to build a cubic bezier path between two anchor points with a soft curve.
  const sourcePath = (y) =>
    `M${SRC_RIGHT} ${y} C ${SRC_RIGHT + 70} ${y}, ${CORE_X - 50} ${CORE_Y_MID}, ${CORE_X} ${CORE_Y_MID}`;
  const buyerPath = (y) =>
    `M${CORE_RIGHT} ${CORE_Y_MID} C ${CORE_RIGHT + 50} ${CORE_Y_MID}, ${BUYER_X - 70} ${y}, ${BUYER_X} ${y}`;

  return (
    <div className="relative w-full h-full" data-testid="hero-dataflow">
      <svg viewBox="0 0 640 410" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
        <defs>
          <linearGradient id="line-grad" x1="0" x2="1">
            <stop offset="0%" stopColor="#0055FF" stopOpacity="0" />
            <stop offset="50%" stopColor="#0055FF" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#0055FF" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="packet-glow">
            <stop offset="0%" stopColor="#0055FF" stopOpacity="1" />
            <stop offset="50%" stopColor="#0055FF" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#0055FF" stopOpacity="0" />
          </radialGradient>
          <filter id="soft-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* ============================================================
            BACKGROUND FLOW LINES (static, very faint) — drawn FIRST so
            cards & packets render on top
           ============================================================ */}
        {SOURCES.map((s, i) => (
          <path
            key={`sl${i}`}
            d={sourcePath(s.y)}
            stroke="#CBD5E1"
            strokeWidth="1"
            fill="none"
            strokeDasharray="3 4"
            opacity="0.55"
          />
        ))}
        {BUYERS.map((b, i) => (
          <path
            key={`bl${i}`}
            d={buyerPath(b.y)}
            stroke="#CBD5E1"
            strokeWidth="1"
            fill="none"
            strokeDasharray="3 4"
            opacity="0.55"
          />
        ))}

        {/* ============================================================
            ACTIVE TRAVELLING PACKETS — left side (source → core)
            One packet per source line, all running at CYCLE seconds with
            staggered begin times so they form a steady waterfall.
           ============================================================ */}
        {SOURCES.map((s, i) => {
          const begin = i * (CYCLE / SOURCES.length) * 0.45; // gentle stagger
          return (
            <g key={`sp${i}`}>
              {/* halo */}
              <circle r="7" fill="url(#packet-glow)" opacity="0.6">
                <animateMotion dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${begin}s`} keyPoints="0;1;1" keyTimes="0;0.42;1" calcMode="linear" path={sourcePath(s.y)} />
                <animate attributeName="opacity" dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${begin}s`} values="0;0.7;0.7;0" keyTimes="0;0.08;0.4;0.45" />
              </circle>
              {/* core dot */}
              <circle r="3.2" fill="#0055FF" filter="url(#soft-glow)">
                <animateMotion dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${begin}s`} keyPoints="0;1;1" keyTimes="0;0.42;1" calcMode="linear" path={sourcePath(s.y)} />
                <animate attributeName="opacity" dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${begin}s`} values="0;1;1;0;0" keyTimes="0;0.04;0.4;0.45;1" />
              </circle>
            </g>
          );
        })}

        {/* ============================================================
            ACTIVE TRAVELLING PACKETS — right side (core → buyer)
            Each buyer packet's begin = source begin + handoff lag so
            it visually "continues" the relay.
           ============================================================ */}
        {BUYERS.map((b, i) => {
          const begin = i * (CYCLE / BUYERS.length) * 0.45 + HANDOFF_GAP;
          return (
            <g key={`bp${i}`}>
              <circle r="7" fill="url(#packet-glow)" opacity="0.6">
                <animateMotion dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${begin}s`} keyPoints="0;1;1" keyTimes="0;0.42;1" calcMode="linear" path={buyerPath(b.y)} />
                <animate attributeName="opacity" dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${begin}s`} values="0;0.7;0.7;0" keyTimes="0;0.08;0.4;0.45" />
              </circle>
              <circle r="3.2" fill="#0055FF" filter="url(#soft-glow)">
                <animateMotion dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${begin}s`} keyPoints="0;1;1" keyTimes="0;0.42;1" calcMode="linear" path={buyerPath(b.y)} />
                <animate attributeName="opacity" dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${begin}s`} values="0;1;1;0;0" keyTimes="0;0.04;0.4;0.45;1" />
              </circle>
            </g>
          );
        })}

        {/* ============================================================
            SOURCE NODES (left column)
           ============================================================ */}
        {SOURCES.map((n, i) => (
          <g key={`s${i}`}>
            <rect x={SRC_X} y={n.y - 16} width={SRC_W} height="32" rx="8" fill="#fff" stroke="#E2E8F0" strokeWidth="1" />
            <circle cx={SRC_X + 16} cy={n.y} r="3" fill="#0055FF">
              <animate attributeName="opacity" dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${i * 0.45}s`} values="0.35;1;0.35" keyTimes="0;0.08;1" />
            </circle>
            <text x={SRC_X + 28} y={n.y + 4} fontSize="11" fill="#0F172A" fontFamily="Instrument Sans">
              {n.label}
            </text>
          </g>
        ))}

        {/* ============================================================
            CONNEXIS PING TREE — centre node with absorb ring
           ============================================================ */}
        <g>
          {/* outer absorb ring — pulses each time a packet arrives */}
          <rect
            x={CORE_X - 4}
            y={CORE_Y_MID - 44}
            width={CORE_W + 8}
            height="88"
            rx="14"
            fill="none"
            stroke="#0055FF"
            strokeWidth="1.2"
            opacity="0"
          >
            <animate attributeName="opacity" dur={`${CYCLE / SOURCES.length}s`} repeatCount="indefinite" values="0;0.55;0" keyTimes="0;0.25;1" />
            <animate attributeName="stroke-width" dur={`${CYCLE / SOURCES.length}s`} repeatCount="indefinite" values="1;2.4;1" keyTimes="0;0.25;1" />
          </rect>

          <rect x={CORE_X} y={CORE_Y_MID - 40} width={CORE_W} height="80" rx="12" fill="#0F172A" />
          <text x={CORE_X + CORE_W / 2} y={CORE_Y_MID - 8} textAnchor="middle" fontSize="10.5" letterSpacing="1.2" fill="#94A3B8" fontFamily="Instrument Sans">
            CONNEXIS
          </text>
          <text x={CORE_X + CORE_W / 2} y={CORE_Y_MID + 12} textAnchor="middle" fontSize="13" fill="#fff" fontFamily="Instrument Sans" fontWeight="600">
            Routing
          </text>
          {/* throbbing core indicator */}
          <circle cx={CORE_X + CORE_W / 2} cy={CORE_Y_MID + 28} r="2.5" fill="#22D3A6">
            <animate attributeName="opacity" values="0.4;1;0.4" dur="1.6s" repeatCount="indefinite" />
          </circle>
        </g>

        {/* ============================================================
            BUYER NODES (right column) — two-line card so label & price
            can never collide regardless of name length
           ============================================================ */}
        {BUYERS.map((b, i) => {
          // rotate which buyer is "winning" across the cycle so they all feel live
          const winBegin = i * (CYCLE / BUYERS.length);
          return (
            <g key={`b${i}`}>
              {/* winner ring — only visible during this buyer's window */}
              <rect
                x={BUYER_X - 4}
                y={b.y - BUYER_H / 2 - 4}
                width={BUYER_W + 8}
                height={BUYER_H + 8}
                rx="12"
                fill="none"
                stroke="#22D3A6"
                strokeWidth="1.4"
                opacity="0"
              >
                <animate attributeName="opacity" dur={`${CYCLE}s`} repeatCount="indefinite" begin={`${winBegin}s`} values="0;0;0.85;0.85;0" keyTimes="0;0.1;0.18;0.3;0.4" />
              </rect>

              <rect
                x={BUYER_X}
                y={b.y - BUYER_H / 2}
                width={BUYER_W}
                height={BUYER_H}
                rx="10"
                fill="#fff"
                stroke="#E2E8F0"
                strokeWidth="1"
              />
              {/* left status dot — flashes when its packet arrives */}
              <circle cx={BUYER_X + 12} cy={b.y} r="2.5" fill="#0055FF">
                <animate
                  attributeName="opacity"
                  dur={`${CYCLE}s`}
                  repeatCount="indefinite"
                  begin={`${i * (CYCLE / BUYERS.length) * 0.45 + HANDOFF_GAP + SRC_DUR * 0.4}s`}
                  values="0.35;1;0.35"
                  keyTimes="0;0.12;1"
                />
              </circle>
              {/* line 1 — buyer label */}
              <text
                x={BUYER_X + 24}
                y={b.y - 4}
                fontSize="11"
                fill="#0F172A"
                fontFamily="Instrument Sans"
                fontWeight="500"
              >
                {b.label}
              </text>
              {/* line 2 — fixed price in mono, accent colour */}
              <text
                x={BUYER_X + 24}
                y={b.y + 12}
                fontSize="11"
                fontWeight="600"
                fill="#0055FF"
                fontFamily="JetBrains Mono"
              >
                {b.price}
              </text>
              {/* tiny "CPL" caption right-aligned — fixed price per lead */}
              <text
                x={BUYER_X + BUYER_W - 12}
                y={b.y + 12}
                fontSize="8.5"
                letterSpacing="1.4"
                fill="#94A3B8"
                fontFamily="JetBrains Mono"
                textAnchor="end"
              >
                CPL
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
